import { type BaseViewModeProps } from '@exam-center/components';
import {
  CalendarTodayIcon,
  Flex,
  Text,
  ReadonlyField,
  type ReadonlyFieldProps,
  FormControl,
  RadioGroup,
  FormRadio,
  FormDateInput,
  Box,
} from '@shared/components';

type ViewModeProps = BaseViewModeProps<
  Omit<ReadonlyFieldProps, 'content' | 'maxLines'> & {
    endAdornment?: React.ReactNode;
  }
>;

interface InputModeProps {
  mode: 'input';
  assign: {
    name: string;
    hiddenHelperText?: boolean;
  };
  date: {
    name: string;
    required: boolean;
    hidden?: boolean;
  };
  slotProps?: {
    label?: string;
    unassignedLabel?: string;
    assignedLabel?: string;
    endAdornment?: React.ReactNode;
  };
}

export type BlockedEndDateRendererProps = InputModeProps | ViewModeProps;

export default function BlockedEndDateRenderer(props: Readonly<BlockedEndDateRendererProps>) {
  switch (props.mode) {
    case 'input': {
      const { assign, date, slotProps } = props;

      const {
        label = 'วันที่สิ้นสุดปฏิบัติหน้าที่',
        unassignedLabel = 'ไม่มีกำหนด',
        assignedLabel = 'กำหนดวันที่สิ้นสุดปฏิบัติหน้าที่',
      } = slotProps || {};

      return (
        <FormControl>
          <RadioGroup
            name={assign.name}
            sx={(theme) => ({
              gap: theme.spacing(4),
            })}
          >
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
              <FormRadio
                {...assign}
                label={unassignedLabel}
                value='false'
                radioProps={{
                  sx: (theme) => ({
                    py: theme.spacing(0),
                  }),
                }}
              />
              {!assign.hiddenHelperText ? (
                <Text
                  variant='caption'
                  sx={(theme) => ({ ml: theme.spacing(8), color: theme.palette.text.secondary })}
                >
                  ยังไม่มีกำหนดวันที่สิ้นสุดปฏิบัติหน้าที่
                </Text>
              ) : null}
            </Flex>
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
              <FormRadio
                {...assign}
                label={assignedLabel}
                value='true'
                radioProps={{
                  sx: (theme) => ({
                    py: theme.spacing(0),
                  }),
                }}
              />
              <Flex
                sx={(theme) => ({
                  flexDirection: 'column',
                  ml: theme.spacing(8),
                  gap: theme.spacing(2.5),
                })}
              >
                {!assign.hiddenHelperText ? (
                  <Text variant='caption' sx={(theme) => ({ color: theme.palette.text.secondary })}>
                    คำนวณจากระยะเวลาปฏิบัติหน้าที่หรือระบุวันที่สิ้นสุดปฏิบัติหน้าที่ด้วยตนเอง
                  </Text>
                ) : null}
                {!date.hidden ? (
                  <Flex
                    sx={(theme) => ({
                      border: '1px solid',
                      borderColor: theme.palette.grey[300],
                      borderRadius: theme.shape.rounded(1.5),
                      backgroundColor: theme.palette.grey[50],
                      p: theme.spacing(4),
                      gap: theme.spacing(4),
                      width: 'fit-content',
                    })}
                  >
                    <Box
                      sx={{
                        width: '212px',
                      }}
                    >
                      <FormDateInput
                        {...date}
                        label={label}
                        placeholder='DD-MM-YYYY'
                        inputFormat='DD-MM-YYYY'
                        minDate={new Date()}
                      />
                    </Box>
                    {slotProps?.endAdornment}
                  </Flex>
                ) : null}
              </Flex>
            </Flex>
          </RadioGroup>
        </FormControl>
      );
    }
    case 'view': {
      const { value, slotProps } = props;
      const { ...readonlyFieldProps } = slotProps || {};
      return (
        <Flex sx={(theme) => ({ alignItems: 'flex-end', gap: theme.spacing(4) })}>
          <ReadonlyField
            content={
              <Flex
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Text component='span'>{value}</Text>
                <CalendarTodayIcon sx={(theme) => ({ color: theme.palette.text.secondary })} />
              </Flex>
            }
            {...readonlyFieldProps}
          />
          {slotProps?.endAdornment}
        </Flex>
      );
    }
  }
}
